<template>
  <div class="time-picker-container">
    <div class="time-picker">
      <label>{{ $t('base.period') }} {{ $t('base.from') }}</label>
      <div class="custom-time-picker">
        <input
          type="text"
          v-model="startHour"
          @input="onStartHourInput"
          @blur="onStartHourBlur"
          @focus="onFocus"
          :maxlength="2"
        />
        <span>:</span>
        <input
          type="text"
          v-model="startMinute"
          @input="onMinuteInput('startMinute')"
          @blur="onMinuteBlur('startMinute')"
          @focus="onFocus"
          maxlength="2"
        />
        <select v-if="isUSFormat" v-model="startPeriod" @change="onStartPeriodChange" class="period-select">
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
      </div>
    </div>
    <div class="time-picker">
      <label>{{ $t('base.period') }} {{ $t('base.to') }}</label>
      <div class="custom-time-picker">
        <input
          type="text"
          v-model="endHour"
          @input="onEndHourInput"
          @blur="onEndHourBlur"
          @focus="onFocus"
          :maxlength="2"
        />
        <span>:</span>
        <input
          type="text"
          v-model="endMinute"
          @input="onMinuteInput('endMinute')"
          @blur="onMinuteBlur('endMinute')"
          @focus="onFocus"
          maxlength="2"
        />
        <select v-if="isUSFormat" v-model="endPeriod" @change="emitTimeRange" class="period-select">
          <option value="AM" :disabled="startPeriod === 'PM'">AM</option>
          <option value="PM">PM</option>
        </select>
      </div>
    </div>
    <p v-if="timeInvalid" class="error-text">
      {{ $t('errors.timeRangeInvalid') }}
    </p>
  </div>
</template>

<script>
export default {
  name: 'TimeField',

  props: {
    from: {
      type: String,
      default: '06:00:00',
    },
    to: {
      type: String,
      default: '11:00:00',
    },
  },

  data () {
    const isUS = this.$store.getters['account/lang'] === 'us'
    const [fromHour, fromMinute] = this.from.split(':')
    const [toHour, toMinute] = this.to.split(':')

    if (isUS) {
      const start = this.convertTo12Hour(parseInt(fromHour))
      const end = this.convertTo12Hour(parseInt(toHour))
      return {
        startHour: start.hour.padStart(2, '0'),
        startMinute: fromMinute.padStart(2, '0'),
        startPeriod: start.period,
        endHour: end.hour.padStart(2, '0'),
        endMinute: toMinute.padStart(2, '0'),
        endPeriod: end.period,
        timeInvalid: false,
        isFocused: false,
      }
    } else {
      return {
        startHour: fromHour.padStart(2, '0'),
        startMinute: fromMinute.padStart(2, '0'),
        startPeriod: 'AM',
        endHour: toHour.padStart(2, '0'),
        endMinute: toMinute.padStart(2, '0'),
        endPeriod: 'PM',
        timeInvalid: false,
        isFocused: false,
      }
    }
  },

  computed: {
    currentLang () {
      return this.$store.getters['account/lang']
    },
    isUSFormat () {
      return this.currentLang === 'us'
    }
  },

  methods: {
    convertTo12Hour (hour24) {
      if (hour24 === 0) return { hour: '12', period: 'AM' }
      if (hour24 < 12) return { hour: String(hour24), period: 'AM' }
      if (hour24 === 12) return { hour: '12', period: 'PM' }
      return { hour: String(hour24 - 12), period: 'PM' }
    },

    convertTo24Hour (hour12, period) {
      let hour = parseInt(hour12)
      if (period === 'AM') {
        if (hour === 12) hour = 0
      } else {
        if (hour !== 12) hour += 12
      }
      return hour
    },

    get24HourTime (hour, minute, period) {
      if (this.isUSFormat) {
        const hour24 = this.convertTo24Hour(hour, period)
        return { hour: hour24, minute: parseInt(minute) }
      }
      return { hour: parseInt(hour), minute: parseInt(minute) }
    },

    onFocus () {
      this.isFocused = true
    },

    // ---- Hour inputs ----

    onStartHourInput () {
      // Strip non-digits
      this.startHour = this.startHour.replace(/\D/g, '')
      // Clamp only when 2 digits entered
      if (this.startHour.length === 2) {
        this.startHour = this.clampHour(this.startHour)
      }
    },

    onStartHourBlur () {
      this.isFocused = false
      if (this.startHour === '' || this.startHour === '0') {
        this.startHour = this.isUSFormat ? '01' : '00'
      } else {
        this.startHour = this.clampHour(this.startHour).padStart(2, '0')
      }
      this.emitTimeRange()
    },

    onEndHourInput () {
      this.endHour = this.endHour.replace(/\D/g, '')
      if (this.endHour.length === 2) {
        this.endHour = this.clampHour(this.endHour)
      }
    },

    onEndHourBlur () {
      this.isFocused = false
      if (this.endHour === '' || this.endHour === '0') {
        this.endHour = this.isUSFormat ? '01' : '00'
      } else {
        this.endHour = this.clampHour(this.endHour).padStart(2, '0')
      }
      this.adjustTimeRangeIfNeeded()
      this.emitTimeRange()
    },

    clampHour (val) {
      let n = parseInt(val)
      if (isNaN(n)) return this.isUSFormat ? '01' : '00'
      if (this.isUSFormat) {
        if (n < 1) n = 1
        if (n > 12) n = 12
      } else {
        if (n < 0) n = 0
        if (n > 23) n = 23
      }
      return String(n)
    },

    // ---- Minute inputs ----

    onMinuteInput (field) {
      this[field] = this[field].replace(/\D/g, '')
      if (this[field].length === 2) {
        let n = parseInt(this[field])
        if (n > 59) this[field] = '59'
      }
    },

    onMinuteBlur (field) {
      this.isFocused = false
      if (this[field] === '') {
        this[field] = '00'
      } else {
        let n = parseInt(this[field])
        if (isNaN(n) || n < 0) n = 0
        if (n > 59) n = 59
        this[field] = String(n).padStart(2, '0')
      }
      this.adjustTimeRangeIfNeeded()
      this.emitTimeRange()
    },

    // ---- Period ----

    onStartPeriodChange () {
      if (this.startPeriod === 'PM' && this.endPeriod === 'AM') {
        this.endPeriod = 'PM'
      }
      this.emitTimeRange()
    },

    // ---- Range validation ----

    adjustTimeRangeIfNeeded () {
      if (this.startHour === '' || this.startMinute === '' ||
          this.endHour === '' || this.endMinute === '') {
        this.timeInvalid = false
        return
      }

      const startTime = this.get24HourTime(this.startHour, this.startMinute, this.startPeriod)
      const endTime = this.get24HourTime(this.endHour, this.endMinute, this.endPeriod)

      if (startTime.hour > endTime.hour ||
         (startTime.hour === endTime.hour && startTime.minute >= endTime.minute)) {
        this.endHour = this.startHour
        this.endMinute = this.startMinute
        if (this.isUSFormat) {
          this.endPeriod = this.startPeriod
        }
      }
      this.timeInvalid = false
    },

    // ---- Emit ----

    emitTimeRange () {
      const startH = (this.startHour || '00').padStart(2, '0')
      const startM = (this.startMinute || '00').padStart(2, '0')
      const endH = (this.endHour || '00').padStart(2, '0')
      const endM = (this.endMinute || '00').padStart(2, '0')

      let finalStartH, finalEndH
      if (this.isUSFormat) {
        finalStartH = String(this.convertTo24Hour(startH, this.startPeriod)).padStart(2, '0')
        finalEndH = String(this.convertTo24Hour(endH, this.endPeriod)).padStart(2, '0')
      } else {
        finalStartH = startH
        finalEndH = endH
      }

      this.$emit('updateTime',
        `${finalStartH}:${startM}:00`,
        `${finalEndH}:${endM}:00`
      )
    },
  },

  watch: {
    currentLang: {
      handler (newLang, oldLang) {
        if (newLang !== oldLang) {
          if (newLang === 'us') {
            const startConverted = this.convertTo12Hour(parseInt(this.startHour))
            const endConverted = this.convertTo12Hour(parseInt(this.endHour))
            this.startHour = startConverted.hour
            this.startPeriod = startConverted.period
            this.endHour = endConverted.hour
            this.endPeriod = endConverted.period
          } else {
            this.startHour = String(this.convertTo24Hour(this.startHour, this.startPeriod)).padStart(2, '0')
            this.endHour = String(this.convertTo24Hour(this.endHour, this.endPeriod)).padStart(2, '0')
          }
          this.emitTimeRange()
        }
      },
      immediate: false
    }
  },

  mounted () {
    this.emitTimeRange()
  },
}
</script>

<style scoped>
.time-picker-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.time-picker {
  display: flex;
  gap: 5px;
  align-items: center;
  justify-content: space-between;
}
.time-picker label {
  font-size: 1.6rem;
  font-weight: 600;
}
.custom-time-picker {
  display: flex;
  align-items: center;
  gap: 5px;
}
input[type="text"] {
  background: #eeee;
  color: #000;
  padding: 0.5rem;
  font-size: 1.6rem;
  width: 40px;
  text-align: center;
  cursor: pointer;
  font-weight: 500;
  border-radius: 1rem;
  border: none;
  outline: none;
}
input[type="text"]:focus {
  background: #DDF2E3;
}
.period-select {
  background: #F4F4F4;
  color: #000;
  padding: 0.5rem;
  font-size: 1.6rem;
  font-weight: 500;
  cursor: pointer;
  border: none;
  border-radius: 0;
}
.period-select option:disabled {
  background-color: #e0e0e0;
  color: #999;
}
.error-text {
  color: red;
  font-size: 12px;
}
</style>